import { Badge } from '@eriko/ui/src/components/ui/badge';
import { Button } from '@eriko/ui/src/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@eriko/ui/src/components/ui/card';
import { Mail, Shield, Building2, CheckCircle, AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resendVerify } from '../../../api/auth';
import { useAuthStore } from '../../../stores/authStore';

export function ProfileTab() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const user = useAuthStore((s) => s.user);
  const organization = useAuthStore((s) => s.organization);
  const role = useAuthStore((s) => s.role);
  const [resendState, setResendState] = useState<'idle' | 'loading' | 'sent' | 'error'>('idle');

  const initials = user?.fullName
    ? user.fullName
        .split(' ')
        .map((w) => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '??';

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('profile.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-6">
            {/* Avatar */}
            <div className="w-16 h-16 bg-gradient-to-br from-brand-400 to-emerald-500 flex items-center justify-center text-white text-xl font-bold shrink-0">
              {initials}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0 space-y-4">
              {/* Name */}
              <div>
                <p className="text-xs font-medium text-surface-400 uppercase tracking-wider mb-1">
                  {t('profile.fullName')}
                </p>
                <p className="text-sm font-semibold text-surface-900">
                  {user?.fullName || '—'}
                </p>
              </div>

              {/* Email */}
              <div>
                <p className="text-xs font-medium text-surface-400 uppercase tracking-wider mb-1">
                  {t('profile.email')}
                </p>
                <div className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-surface-400" strokeWidth={1.8} />
                  <p className="text-sm text-surface-700">{user?.email || '—'}</p>
                  {user?.emailVerified ? (
                    <span className="inline-flex items-center gap-1 text-xs text-green-600">
                      <CheckCircle className="w-3.5 h-3.5" strokeWidth={2} />
                      {t('profile.emailVerified')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                      <AlertCircle className="w-3.5 h-3.5" strokeWidth={2} />
                      {t('profile.emailNotVerified')}
                    </span>
                  )}
                </div>
                {user && !user.emailVerified && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 text-xs h-7"
                    disabled={resendState === 'loading' || resendState === 'sent'}
                    onClick={async () => {
                      setResendState('loading');
                      try {
                        await resendVerify();
                        setResendState('sent');
                      } catch {
                        setResendState('error');
                      }
                    }}
                  >
                    {resendState === 'loading'
                      ? t('profile.sending')
                      : resendState === 'sent'
                        ? t('profile.emailSent')
                        : resendState === 'error'
                          ? t('profile.resendError')
                          : t('profile.resendVerify')}
                  </Button>
                )}
              </div>

              {/* Role */}
              <div>
                <p className="text-xs font-medium text-surface-400 uppercase tracking-wider mb-1">
                  {t('profile.role')}
                </p>
                <div className="flex items-center gap-2">
                  <Shield className="w-3.5 h-3.5 text-surface-400" strokeWidth={1.8} />
                  <Badge
                    variant="outline"
                    className={
                      role === 'OWNER'
                        ? 'text-brand-700 border-brand-200 bg-brand-50'
                        : role === 'ADMIN'
                          ? 'text-blue-700 border-blue-200 bg-blue-50'
                          : 'text-surface-600 border-surface-200 bg-surface-50'
                    }
                  >
                    {tc(`roles.${role || ''}`) || role}
                  </Badge>
                </div>
              </div>

              {/* Organization */}
              <div>
                <p className="text-xs font-medium text-surface-400 uppercase tracking-wider mb-1">
                  {t('profile.organization')}
                </p>
                <div className="flex items-center gap-2">
                  <Building2 className="w-3.5 h-3.5 text-surface-400" strokeWidth={1.8} />
                  <p className="text-sm text-surface-700">
                    {organization?.name || '—'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
